import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Facebook, Instagram } from 'lucide-react';
import LogoMark from './LogoMark';
import { businesses, companyInfo } from '../mockData';

const Footer = () => {
  const year = new Date().getFullYear();

  return (
    <footer
      className="bg-brand-oxford text-brand-alabaster relative overflow-hidden"
      data-testid="site-footer"
    >
      <div className="container-editorial pt-24 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-10">
          {/* Wordmark + statement */}
          <div className="lg:col-span-5">
            <div className="text-brand-alabaster mb-6">
              <div className="inline-flex items-center gap-3">
                <img
                  src="/images/logo/lockene-mosaic.png"
                  alt=""
                  aria-hidden="true"
                  className="h-10 w-10 object-contain"
                />
                <span className="font-display text-3xl tracking-tight leading-none">Lockene</span>
              </div>
            </div>
            <p className="font-display text-3xl md:text-4xl leading-[1.15] tracking-tight max-w-lg">
              {companyInfo.tagline}
            </p>
            <p className="mt-6 text-sm text-brand-alabaster/70 max-w-md leading-relaxed">
              {companyInfo.description}
            </p>
          </div>

          {/* Businesses */}
          <nav className="lg:col-span-3" aria-label="Businesses">
            <div className="eyebrow text-brand-alabaster/60 mb-5">Businesses</div>
            <ul className="space-y-3">
              {businesses.map((b) => (
                <li key={b.id}>
                  <Link
                    to={`/business/${b.id}`}
                    data-testid={`footer-nav-${b.id}`}
                    className="text-sm text-brand-alabaster/85 hover:text-brand-gold transition-colors inline-flex items-center gap-2 group"
                  >
                    <span
                      className="w-1.5 h-1.5"
                      style={{ backgroundColor: `rgb(var(--sub-${b.subBrand}))` }}
                      aria-hidden
                    />
                    {b.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Company */}
          <nav className="lg:col-span-2" aria-label="Company">
            <div className="eyebrow text-brand-alabaster/60 mb-5">Company</div>
            <ul className="space-y-3">
              <li><Link to="/about" className="text-sm text-brand-alabaster/85 hover:text-brand-gold transition-colors" data-testid="footer-nav-about">About</Link></li>
              <li><Link to="/case-studies" className="text-sm text-brand-alabaster/85 hover:text-brand-gold transition-colors" data-testid="footer-nav-case-studies">Case Studies</Link></li>
              <li><Link to="/insights" className="text-sm text-brand-alabaster/85 hover:text-brand-gold transition-colors" data-testid="footer-nav-insights">Insights</Link></li>
              <li><Link to="/contact" className="text-sm text-brand-alabaster/85 hover:text-brand-gold transition-colors" data-testid="footer-nav-contact">Contact</Link></li>
              <li><Link to="/privacy" className="text-sm text-brand-alabaster/85 hover:text-brand-gold transition-colors" data-testid="footer-nav-privacy">Privacy</Link></li>
            </ul>
          </nav>

          {/* Contact + offices */}
          <div className="lg:col-span-2">
            <div className="eyebrow text-brand-alabaster/60 mb-5">Contact</div>
            <address className="not-italic text-sm text-brand-alabaster/85 space-y-2">
              <a href={`mailto:${companyInfo.primaryEmail}`} className="block hover:text-brand-gold transition-colors" data-testid="footer-email">
                {companyInfo.primaryEmail}
              </a>
              <a href={`tel:${companyInfo.primaryPhone.replace(/\s+/g, '')}`} className="block hover:text-brand-gold transition-colors" data-testid="footer-phone">
                {companyInfo.primaryPhone}
              </a>
            </address>
            <div className="mt-6 flex gap-3">
              <a
                href={companyInfo.socials.linkedin}
                target="_blank"
                rel="noreferrer noopener"
                className="w-9 h-9 border border-brand-alabaster/25 flex items-center justify-center hover:bg-brand-alabaster hover:text-brand-oxford transition-colors focus-ring"
                aria-label="Lockene on LinkedIn"
                data-testid="footer-linkedin"
              >
                <Linkedin size={16} strokeWidth={1.5} />
              </a>
              <a
                href={companyInfo.socials.facebook}
                target="_blank"
                rel="noreferrer noopener"
                className="w-9 h-9 border border-brand-alabaster/25 flex items-center justify-center hover:bg-brand-alabaster hover:text-brand-oxford transition-colors focus-ring"
                aria-label="Lockene on Facebook"
                data-testid="footer-facebook"
              >
                <Facebook size={16} strokeWidth={1.5} />
              </a>
              <a
                href={companyInfo.socials.instagram}
                target="_blank"
                rel="noreferrer noopener"
                className="w-9 h-9 border border-brand-alabaster/25 flex items-center justify-center hover:bg-brand-alabaster hover:text-brand-oxford transition-colors focus-ring"
                aria-label="Lockene on Instagram"
                data-testid="footer-instagram"
              >
                <Instagram size={16} strokeWidth={1.5} />
              </a>
            </div>
          </div>
        </div>

        {/* Offices strip */}
        <div className="mt-16 border-t border-brand-alabaster/15 pt-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {companyInfo.locations.map((loc) => (
            <div key={loc.city}>
              <div className="text-[11px] tracking-[0.18em] uppercase text-brand-alabaster/50 font-bold">
                {loc.role}
              </div>
              <div className="font-display text-2xl mt-1">{loc.city}</div>
              <div className="text-sm text-brand-alabaster/70">{loc.country}</div>
            </div>
          ))}
        </div>

        {/* Colophon */}
        <div className="mt-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-t border-brand-alabaster/15 pt-6">
          <p className="text-xs text-brand-alabaster/60">
            © {year} {companyInfo.legalName}. All rights reserved.
          </p>
          <p className="text-xs text-brand-alabaster/50 tracking-wide">
            Established {companyInfo.founded} · Registered in India & the United States
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
